/**
 * Página SubscriptionSuccess
 * Página de éxito después de completar una suscripción
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiCheck, FiHome, FiUser, FiArrowRight } from 'react-icons/fi';
import Button from '../../components/UI/Button';
import AmarsiLogo from '../../components/Icons/AmarsiLogo';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import { SUCCESS_MESSAGES } from '../../constants/subscriptions';
import mercadoPagoService from '../../services/mercadopago_compatible';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { subscription, refreshSubscription } = useSubscription(user?.id);
  
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [subscriptionUpdated, setSubscriptionUpdated] = useState(false);

  /**
   * Extraer datos del payment_id de la URL y actualizar la suscripción
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentId = params.get('payment_id');
    const externalReference = params.get('external_reference');
    const planId = params.get('plan_id');
    const preapprovalId = params.get('preapproval_id'); // Agregar soporte para preapproval_id
    
    // También intentar obtener el preapproval_id del sessionStorage (por si viene de la redirección)
    const storedPreapprovalId = sessionStorage.getItem('mp_preapproval_id');
    const finalPreapprovalId = preapprovalId || storedPreapprovalId;

    console.log('🔍 Debug - Parámetros URL:', {
      paymentId,
      externalReference,
      planId,
      preapprovalId: finalPreapprovalId,
      storedPreapprovalId
    });

    // Si venimos de Mercado Pago, dar más tiempo para la autenticación
    const isMercadoPagoReturn = finalPreapprovalId || paymentId;
    const loadDelay = isMercadoPagoReturn ? 3000 : 2000; // Más tiempo si es retorno de MP

    if (paymentId || externalReference || planId || finalPreapprovalId) {
      setPaymentData({
        paymentId,
        externalReference,
        planId,
        preapprovalId: finalPreapprovalId // Agregar preapproval_id a los datos
      });
    }

    // Función para actualizar la suscripción después del retorno
    const updateSubscriptionAfterReturn = async () => {
      if (finalPreapprovalId && isAuthenticated && user?.id && !subscriptionUpdated) {
        try {
          console.log('🔄 Actualizando suscripción después del retorno de Mercado Pago...', {
            preapprovalId: finalPreapprovalId,
            userId: user.id
          });
          
          // Llamar al servicio para actualizar la suscripción
          const result = await mercadoPagoService.updateSubscriptionAfterReturn(finalPreapprovalId);
          
          if (result.success) {
            console.log('✅ Suscripción actualizada exitosamente:', result);
            setSubscriptionUpdated(true);
            
            // Limpiar el sessionStorage
            sessionStorage.removeItem('mp_preapproval_id');
            
            // Refrescar la suscripción en el contexto
            await refreshSubscription();
          } else {
            console.error('❌ Error actualizando suscripción:', result.error);
          }
        } catch (error) {
          console.error('❌ Error en updateSubscriptionAfterReturn:', error);
        }
      }
    };

    // Simular carga y refrescar suscripción
    const timer = setTimeout(async () => {
      if (isAuthenticated && user?.id) {
        console.log('🔄 Refrescando suscripción después del retorno de Mercado Pago...');
        await refreshSubscription();
        
        // Intentar actualizar la suscripción si tenemos preapproval_id
        await updateSubscriptionAfterReturn();
      } else if (!isAuthenticated && isMercadoPagoReturn) {
        console.log('⚠️ Usuario no autenticado después de retorno de Mercado Pago, esperando más tiempo...');
        // Dar tiempo adicional para que se complete la autenticación
        setTimeout(async () => {
          if (isAuthenticated && user?.id) {
            console.log('🔄 Usuario autenticado, refrescando suscripción...');
            await refreshSubscription();
            await updateSubscriptionAfterReturn();
          }
          setLoading(false);
        }, 2000);
        return; // No hacer setLoading(false) aún
      }
      setLoading(false);
    }, loadDelay);

    return () => clearTimeout(timer);
  }, [location.search, isAuthenticated, user?.id, refreshSubscription, subscriptionUpdated]);

  /**
   * Renderiza el contenido principal
   */
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Procesando tu suscripción...
            </h2>
            <p className="text-gray-600">
              Estamos confirmando los detalles de tu pago.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center gap-3">
                <AmarsiLogo width="32" height="32" />
                <h1 className="text-xl font-bold text-gray-900">Amarsi</h1>
              </div>
              <nav className="flex items-center space-x-4">
                <Link to="/" className="text-gray-600 hover:text-gray-900">
                  Inicio
                </Link>
                <Link to="/premium" className="text-gray-600 hover:text-gray-900">
                  Premium
                </Link>
                {isAuthenticated ? (
                  <Link to="/profile">
                    <Button variant="outline" size="sm">
                      <FiUser className="h-4 w-4 mr-2" />
                      Mi Perfil
                    </Button>
                  </Link>
                ) : (
                  <Link to="/auth/login">
                    <Button variant="primary" size="sm">
                      Iniciar Sesión
                    </Button>
                  </Link>
                )}
              </nav>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            {/* Success Icon */}
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiCheck className="w-12 h-12 text-green-600" />
            </div>

            {/* Success Message */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              ¡Suscripción Exitosa!
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              {SUCCESS_MESSAGES.SUBSCRIPTION_CREATED}
            </p>
          </div>

          {/* Success Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Detalles de tu Suscripción
            </h2>

            {/* Subscription Details */}
            {subscription && (
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">Plan</span>
                  <span className="font-semibold text-gray-900">
                    {subscription.planes?.nombre || 'Premium'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">Estado</span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
                    Activa
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">Monto</span>
                  <span className="font-semibold text-gray-900">
                    ${subscription.monto || '0.00'} {subscription.moneda || 'MXN'}/{subscription.frecuencia || 'mes'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-gray-600">Próxima renovación</span>
                  <span className="font-semibold text-gray-900">
                    {subscription.proxima_renovacion 
                      ? new Date(subscription.proxima_renovacion).toLocaleDateString('es-MX')
                      : 'N/A'
                    }
                  </span>
                </div>
              </div>
            )}

            {/* Payment Details */}
            {paymentData && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">Información del Pago</h3>
                <div className="space-y-1 text-sm text-gray-600">
                  {paymentData.preapprovalId && (
                    <p>ID de Preaprobación: <span className="font-mono">{paymentData.preapprovalId}</span></p>
                  )}
                  {paymentData.paymentId && (
                    <p>ID de Pago: <span className="font-mono">{paymentData.paymentId}</span></p>
                  )}
                  {paymentData.externalReference && (
                    <p>Referencia: <span className="font-mono">{paymentData.externalReference}</span></p>
                  )}
                  <p>Fecha: {new Date().toLocaleDateString('es-MX')}</p>
                </div>
              </div>
            )}
          </div>

          {/* Next Steps */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              ¿Qué sigue?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <FiUser className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Completa tu Perfil</h3>
                <p className="text-gray-600 text-sm">
                  Agrega tu foto y información para conectar con más personas.
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <FiArrowRight className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Explora Características</h3>
                <p className="text-gray-600 text-sm">
                  Descubre todas las funciones premium disponibles.
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <FiHome className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Comienza a Conectar</h3>
                <p className="text-gray-600 text-sm">
                  Empieza a usar las herramientas de matching avanzadas.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isAuthenticated ? (
              <>
                <Link to="/profile">
                  <Button variant="primary" size="lg">
                    Ir a mi Perfil
                    <FiUser className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" size="lg">
                    Explorar Amarsi
                    <FiHome className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/auth/login">
                  <Button variant="primary" size="lg">
                    Iniciar Sesión
                    <FiUser className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" size="lg">
                    Volver al Inicio
                    <FiHome className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </>
            )}
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white mt-20 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center mb-4 md:mb-0 gap-4">
                <AmarsiLogo width='2rem' height='2rem'/>
                <span className="text-gray-900 font-semibold">Amarsi</span>
                <span className="text-gray-500">© {new Date().getFullYear()} Amarsi. Todos los derechos reservados.</span>
              </div>
              <div className="flex space-x-6 text-sm">
                <Link to="/" className="text-gray-600 hover:text-gray-900">
                  Inicio
                </Link>
                <Link to="/premium" className="text-gray-600 hover:text-gray-900">
                  Premium
                </Link>
                <Link to="/politicas-easi" className="text-gray-600 hover:text-gray-900">
                  Políticas
                </Link>
                <Link to="/privacidad" className="text-gray-600 hover:text-gray-900">
                  Privacidad
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  };

  return renderContent();
};

export default SubscriptionSuccess;
